export enum UserRole {
  ADMIN = 'ADMIN',
  HIRING_MANAGER = 'HIRING_MANAGER',
  RECRUITER = 'RECRUITER',
  SUPER_ADMIN = 'SUPER_ADMIN',
  // Renamed from HR (role-tier-model priority, approved 2026-10-02). Zero real
  // accounts held the old name at rename time, measured against both environments.
  HR_MANAGER = 'HR_MANAGER',
  // Enterprise-tier only (not yet enforced in this UI - the tier field itself
  // isn't built yet; these three appear in every customer's role list until it
  // is). HR Manager's scope plus team/user management and the
  // candidates-assessment view.
  CHRO = 'CHRO',
  // Enterprise-tier only. Read-only oversight - no job posting, no user
  // management, no offer recording, no plan/billing access.
  CORPORATE_EXECUTIVE = 'CORPORATE_EXECUTIVE',
  // Enterprise-tier only. User/account administration only - deliberately no
  // candidate/job/offer visibility.
  INTERNAL_TECHNOLOGY = 'INTERNAL_TECHNOLOGY',
}

export const roleLabelMapping = {
  [UserRole.ADMIN]: 'Admin',
  [UserRole.HIRING_MANAGER]: 'Hiring Manager',
  [UserRole.RECRUITER]: 'Recruiter',
  // Missing entirely until now: a Super Admin row in the users table rendered
  // "Unknown" because this map had no case for it, even though the role has
  // existed in the enum and the permissions table all along.
  [UserRole.SUPER_ADMIN]: 'Super Admin',
  [UserRole.HR_MANAGER]: 'HR Manager',
  // Standalone acronym rather than a multi-word name - deliberate: it's already
  // how the title is used verbatim in the business world, the same way nobody
  // expands "CEO" in an org chart.
  [UserRole.CHRO]: 'CHRO',
  [UserRole.CORPORATE_EXECUTIVE]: 'Corporate Executive',
  [UserRole.INTERNAL_TECHNOLOGY]: 'Internal Technology',
};

export enum Permission {
  CAN_ADD_NEW_JOB = 'CAN_ADD_NEW_JOB',
  CAN_SEE_JOB_COMPANY_COLUMN = 'CAN_SEE_JOB_COMPANY_COLUMN',
  CAN_SEE_JOB_RECRUITER_COLUMN = 'CAN_SEE_JOB_RECRUITER_COLUMN',
  CAN_SEE_JOB_HIRING_MANAGER_COLUMN = 'CAN_SEE_JOB_HIRING_MANAGER_COLUMN',
  CAN_SEE_JOB_CUSTOMER_NAME_COLUMN = 'CAN_SEE_JOB_CUSTOMER_NAME_COLUMN',
  CAN_SEE_JOB_POST_CUSTOMER_FIELD = 'CAN_SEE_JOB_POST_CUSTOMER_FIELD',
  CAN_SEE_JOB_ASSIGN_HM_CARD = 'CAN_SEE_JOB_ASSIGN_HM_CARD',
  CAN_SEE_JOB_CANDIDATE_MATCH_BUTTON = 'CAN_SEE_JOB_CANDIDATE_MATCH_BUTTON',

  CAN_SEE_CANDIDATE_SEND_EMAIL_BUTTON = 'CAN_SEE_CANDIDATE_SEND_EMAIL_BUTTON',
  CAN_SEE_CANDIDATE_ACTION_COLUMN = 'CAN_SEE_CANDIDATE_ACTION_COLUMN',
  CAN_SEE_CANDIDATE_REJECT_BUTTON = 'CAN_SEE_CANDIDATE_REJECT_BUTTON',
  CAN_SEE_CANDIDATE_VIEW_HM_FEEDBACK_BUTTON = 'CAN_SEE_CANDIDATE_VIEW_HM_FEEDBACK_BUTTON',
  CAN_SEE_CANDIDATE_ADD_FEEDBACK_BUTTON = 'CAN_SEE_CANDIDATE_ADD_FEEDBACK_BUTTON',
  CAN_SEE_CANDIDATE_NEXT_STAGE_BUTTON = 'CAN_SEE_CANDIDATE_NEXT_STAGE_BUTTON',

  CAN_ADD_NEW_USER = 'CAN_ADD_NEW_USER',
  CAN_SEE_ADD_USER_CUSTOMER_FIELD = 'CAN_SEE_ADD_USER_CUSTOMER_FIELD',
  CAN_SEE_USER_CUSTOMER_COLUMN = 'CAN_SEE_USER_CUSTOMER_COLUMN',
  CAN_SEE_USER_ACTIONS_COLUMN = 'CAN_SEE_USER_ACTIONS_COLUMN',

  CAN_SEE_USER_MANAGMENT_NAVIGATION_BUTTON = 'CAN_SEE_USER_MANAGMENT_NAVIGATION_BUTTON',
  CAN_SEE_MY_PROFILE_NAVIGATION_BUTTON = 'CAN_SEE_MY_PROFILE_NAVIGATION_BUTTON',
  CAN_SEE_CANDIDATES_ASSESSMENT_NAVIGATION_BUTTON = 'CAN_SEE_CANDIDATES_ASSESSMENT_NAVIGATION_BUTTON',

  // Gates the "Customize permissions for this user" action ahead of the
  // per-user override feature (Phase 2). Deliberately its own flag rather
  // than reusing CAN_SEE_USER_ACTIONS_COLUMN: the two happen to be granted
  // to the same roles today, but the gate should be correct because this is
  // who should customize permissions, not because it coincides with who can
  // already edit/reset/delete a user.
  CAN_CUSTOMIZE_USER_PERMISSIONS = 'CAN_CUSTOMIZE_USER_PERMISSIONS',

  // Gates the "Settings" nav item and the customer-config page (fit-score
  // thresholds, branding) - matches exactly who avrio-backend's
  // CustomerService#getCustomer/#updateCustomerConfig authorize: ADMIN for
  // their own customer, SUPER_ADMIN for any.
  CAN_SEE_SETTINGS_NAVIGATION_BUTTON = 'CAN_SEE_SETTINGS_NAVIGATION_BUTTON',

  // Gates the "Mark offer made/accepted" actions - matches avrio-backend's
  // CandidateOfferEventService (SUPER_ADMIN/ADMIN/HIRING_MANAGER/RECRUITER/HR),
  // minus HR, which this frontend's UserRole doesn't model. Nachi confirmed
  // 2026-09-22 this is deliberately NOT scoped to job assignment, unlike
  // CAN_SEE_CANDIDATE_REJECT_BUTTON/CAN_SEE_CANDIDATE_NEXT_STAGE_BUTTON.
  CAN_SEE_OFFER_TRACKING_BUTTONS = 'CAN_SEE_OFFER_TRACKING_BUTTONS',

  // Gates actually recording an offer event (as opposed to
  // CAN_SEE_OFFER_TRACKING_BUTTONS, which only gates whether the buttons are
  // shown at all). role-tier-model priority, approved 2026-10-02: HIRING_MANAGER
  // loses this by direct ruling; HR_MANAGER/CHRO gain it alongside
  // SUPER_ADMIN/ADMIN/RECRUITER.
  CAN_RECORD_OFFER_EVENT = 'CAN_RECORD_OFFER_EVENT',
}

export const permissions = {
  [UserRole.ADMIN]: [
    Permission.CAN_ADD_NEW_JOB,
    Permission.CAN_SEE_JOB_HIRING_MANAGER_COLUMN,
    Permission.CAN_SEE_JOB_RECRUITER_COLUMN,
    Permission.CAN_ADD_NEW_USER,
    Permission.CAN_SEE_USER_ACTIONS_COLUMN,
    Permission.CAN_SEE_USER_MANAGMENT_NAVIGATION_BUTTON,
    Permission.CAN_SEE_JOB_ASSIGN_HM_CARD,
    Permission.CAN_SEE_MY_PROFILE_NAVIGATION_BUTTON,
    Permission.CAN_SEE_CANDIDATE_SEND_EMAIL_BUTTON,
    Permission.CAN_SEE_CANDIDATE_ACTION_COLUMN,
    Permission.CAN_CUSTOMIZE_USER_PERMISSIONS,
    Permission.CAN_SEE_SETTINGS_NAVIGATION_BUTTON,
    Permission.CAN_SEE_OFFER_TRACKING_BUTTONS,
    Permission.CAN_RECORD_OFFER_EVENT,
  ],
  [UserRole.HIRING_MANAGER]: [
    Permission.CAN_SEE_JOB_RECRUITER_COLUMN,
    Permission.CAN_SEE_CANDIDATE_ACTION_COLUMN,
    Permission.CAN_SEE_MY_PROFILE_NAVIGATION_BUTTON,
    Permission.CAN_SEE_CANDIDATE_ADD_FEEDBACK_BUTTON,
    Permission.CAN_SEE_CANDIDATES_ASSESSMENT_NAVIGATION_BUTTON,
    Permission.CAN_SEE_OFFER_TRACKING_BUTTONS,
    // No CAN_RECORD_OFFER_EVENT: removed from HIRING_MANAGER, decided directly
    // by Nachi 2026-10-02.
  ],
  [UserRole.RECRUITER]: [
    Permission.CAN_ADD_NEW_JOB,
    Permission.CAN_SEE_JOB_COMPANY_COLUMN,
    Permission.CAN_SEE_CANDIDATE_SEND_EMAIL_BUTTON,
    Permission.CAN_SEE_MY_PROFILE_NAVIGATION_BUTTON,
    Permission.CAN_SEE_CANDIDATE_ACTION_COLUMN,
    Permission.CAN_SEE_CANDIDATE_REJECT_BUTTON,
    Permission.CAN_SEE_CANDIDATE_VIEW_HM_FEEDBACK_BUTTON,
    Permission.CAN_SEE_CANDIDATE_NEXT_STAGE_BUTTON,
    Permission.CAN_SEE_JOB_ASSIGN_HM_CARD,
    Permission.CAN_SEE_OFFER_TRACKING_BUTTONS,
    Permission.CAN_RECORD_OFFER_EVENT,
  ],
  [UserRole.SUPER_ADMIN]: [
    Permission.CAN_ADD_NEW_JOB,
    Permission.CAN_SEE_JOB_CUSTOMER_NAME_COLUMN,
    Permission.CAN_SEE_JOB_POST_CUSTOMER_FIELD,
    Permission.CAN_ADD_NEW_USER,
    Permission.CAN_SEE_ADD_USER_CUSTOMER_FIELD,
    Permission.CAN_SEE_USER_CUSTOMER_COLUMN,
    Permission.CAN_SEE_USER_ACTIONS_COLUMN,
    Permission.CAN_SEE_USER_MANAGMENT_NAVIGATION_BUTTON,
    Permission.CAN_SEE_CANDIDATE_SEND_EMAIL_BUTTON,
    Permission.CAN_SEE_JOB_ASSIGN_HM_CARD,
    Permission.CAN_SEE_CANDIDATE_ACTION_COLUMN,
    Permission.CAN_SEE_CANDIDATE_REJECT_BUTTON,
    Permission.CAN_SEE_CANDIDATE_VIEW_HM_FEEDBACK_BUTTON,
    Permission.CAN_SEE_CANDIDATE_NEXT_STAGE_BUTTON,
    Permission.CAN_CUSTOMIZE_USER_PERMISSIONS,
    Permission.CAN_SEE_SETTINGS_NAVIGATION_BUTTON,
    Permission.CAN_SEE_OFFER_TRACKING_BUTTONS,
    Permission.CAN_RECORD_OFFER_EVENT,
  ],
  // HR_MANAGER replaces HR. Narrow by design: offer recording plus candidate
  // visibility, nothing else - see the role/tier model proposal's "What the
  // three new roles are proposed to be" for the reasoning.
  [UserRole.HR_MANAGER]: [
    Permission.CAN_SEE_CANDIDATE_ACTION_COLUMN,
    Permission.CAN_SEE_MY_PROFILE_NAVIGATION_BUTTON,
    Permission.CAN_RECORD_OFFER_EVENT,
  ],
  // HR Manager's scope plus team/user management and the candidates-assessment view.
  [UserRole.CHRO]: [
    Permission.CAN_ADD_NEW_USER,
    Permission.CAN_SEE_USER_MANAGMENT_NAVIGATION_BUTTON,
    Permission.CAN_SEE_CANDIDATE_ACTION_COLUMN,
    Permission.CAN_SEE_CANDIDATES_ASSESSMENT_NAVIGATION_BUTTON,
    Permission.CAN_SEE_MY_PROFILE_NAVIGATION_BUTTON,
    Permission.CAN_RECORD_OFFER_EVENT,
  ],
  // Read-only oversight - no job posting, no user management, no offer
  // recording, no plan/billing access.
  [UserRole.CORPORATE_EXECUTIVE]: [
    Permission.CAN_SEE_CANDIDATE_ACTION_COLUMN,
    Permission.CAN_SEE_CANDIDATES_ASSESSMENT_NAVIGATION_BUTTON,
    Permission.CAN_SEE_MY_PROFILE_NAVIGATION_BUTTON,
  ],
  // User/account administration only - deliberately excluded from every
  // candidate/job/offer permission (no legitimate need to see candidate PII).
  [UserRole.INTERNAL_TECHNOLOGY]: [
    Permission.CAN_ADD_NEW_USER,
    Permission.CAN_CUSTOMIZE_USER_PERMISSIONS,
    Permission.CAN_SEE_USER_MANAGMENT_NAVIGATION_BUTTON,
    Permission.CAN_SEE_MY_PROFILE_NAVIGATION_BUTTON,
  ],
};

// SUPER_ADMIN deliberately excluded, same as before - not an assignable role
// through this picker (avrio-backend refuses to let an ADMIN create one).
// The four role-tier-model roles are creatable through this picker without
// tier-gating for now: the tier field itself isn't built yet (a separate PR),
// so there is nothing here yet to condition CHRO/CORPORATE_EXECUTIVE/
// INTERNAL_TECHNOLOGY's Enterprise-only status on.
export const roleOptions = [
  {
    label: roleLabelMapping[UserRole.ADMIN],
    value: UserRole.ADMIN,
  },
  {
    label: roleLabelMapping[UserRole.HIRING_MANAGER],
    value: UserRole.HIRING_MANAGER,
  },
  {
    label: roleLabelMapping[UserRole.RECRUITER],
    value: UserRole.RECRUITER,
  },
  {
    label: roleLabelMapping[UserRole.HR_MANAGER],
    value: UserRole.HR_MANAGER,
  },
  {
    label: roleLabelMapping[UserRole.CHRO],
    value: UserRole.CHRO,
  },
  {
    label: roleLabelMapping[UserRole.CORPORATE_EXECUTIVE],
    value: UserRole.CORPORATE_EXECUTIVE,
  },
  {
    label: roleLabelMapping[UserRole.INTERNAL_TECHNOLOGY],
    value: UserRole.INTERNAL_TECHNOLOGY,
  },
];
